import {ComboboxGroupLabel, ComboboxItem, ComboboxPopover} from '@ariakit/react'
import styled, {css} from 'styled-components'
import {makeSelectItemBaseStyle, makeSelectPopoverBaseStyle} from '@/components/ui/select-atoms/style'
import {makeInputBaseStyle} from '@/components/commons/input-text/style'
import {typography} from '@/theme/typography'

export const StyledComboboxPopover = styled(ComboboxPopover)(
    ({theme}) => css`
        ${makeSelectPopoverBaseStyle(theme)}
        grid-template-rows: auto minmax(0, 1fr);
        gap: ${theme.spacing}px;
    `
)

export const StyledComboboxGroupLabel = styled(ComboboxGroupLabel)(
    ({theme: {palette, typography, spacing}}) => css`
        ${typography.textSm};
        display: flex;
        align-items: center;
        gap: ${spacing * 2}px;
        color: ${palette.neutral[600]};
        font-weight: 500;
        padding: 0 ${spacing * 2}px;
        height: 32px;
        width: 100%;

        & span {
            font-weight: 400;
            color: ${palette.neutral[500]};
        }
    `
)

export const StyledComboboxInputWrapper = styled.div`
    position: relative;
    width: 100%;
`

type StyledComboboxInputTextProps = {
    $size: 'sm' | 'md' | 'lg'
    $hasTypeIcon: boolean
    $hasCloseCombobox: boolean
    $hasError: boolean
}

export const StyledComboboxInputSideComponent = styled.div<{$side: 'left' | 'right'}>(
    ({theme: {spacing}, $side}) => css`
        position: absolute;
        display: flex;
        top: 50%;
        transform: translateY(-50%);

        ${$side == 'left' &&
        css`
            left: ${spacing * 2}px;
        `}

        ${$side == 'right' &&
        css`
            right: ${spacing * 2}px;
        `}
    `
)

export const makeComboboxInputSizeStyle = {
    sm: css`
        height: 28px;
    `,
    md: css`
        height: 32px;
    `,
    lg: css`
        height: 36px;
    `
}

export const StyledComboboxInputText = styled.input<StyledComboboxInputTextProps>(
    ({theme, $size, $hasCloseCombobox, $hasTypeIcon, $hasError}) => css`
        ${makeInputBaseStyle(theme)}
        ${makeComboboxInputSizeStyle[$size]};
        background: ${theme.palette.neutral.white};
        outline: 1px solid ${theme.palette.neutral['300']};
        border-radius: 8px;
        width: 100%;
        transition: ${theme.transition};
        cursor: pointer;
        box-shadow: ${theme.shadows.sm};
        border-radius: 8px;
        &:hover {
            box-shadow: none;
        }

        &:not(&:disabled):hover,
        &:not(&:disabled):focus {
            outline: 1px solid ${$hasError ? theme.palette.danger['300'] : theme.palette.primary['300']};
            box-shadow: ${`${theme.shadows.sm}, 0px 0px 0px 4px ${$hasError ? theme.palette.danger['100'] : theme.palette.primary['100']}`};
        }

        &:disabled {
            background-color: ${theme.palette.neutral['100']};
        }

        ${$hasCloseCombobox &&
        css`
            padding-right: ${theme.spacing * 7}px;
        `}

        ${$hasTypeIcon
            ? css`
                  padding-left: ${theme.spacing * 7}px;
              `
            : css`
                  padding: ${theme.spacing * 2}px ${theme.spacing * 1.5}px;
              `}

        ${$hasError &&
        css`
            outline: 1px solid ${theme.palette.danger['300']};
        `};
    `
)

export const StyledComboboxItem = styled(ComboboxItem)(
    ({theme}) => css`
        ${makeSelectItemBaseStyle(theme)}
        ${typography.textSm};

        &[aria-selected='true'] {
            font-weight: 400;
            background-color: ${theme.palette.neutral[100]};
        }

        &:hover:not([data-active-item='true']) {
            background-color: ${theme.palette.neutral[100]};
        }
    `
)

export const StyledComboboxItemCheckboxWrapper = styled.span(
    () => css`
        min-width: 13px;
    `
)
